/** @jsxImportSource @emotion/react */
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'
import { getClaimMatchStatus } from 'components/lab/billingEraReports/claimState'
import routes from 'components/lab/constants/routes'
import { formatCalendarDate } from 'helpers/dates'
import React from 'react'
import { Link } from 'react-router-dom'
import { BillingTransactionClaim } from 'store/types'

// Opens the matched charge in a new tab so the biller keeps their place in the remit.
export const ClaimIdentifierLink = ({ claim }: { claim: BillingTransactionClaim }) => {
  const label = claim.submittedClaimId || claim.claimNumber || '-'

  if (!claim.chargeId || !claim.chargePatientId) {
    return <>{label}</>
  }

  return (
    <Link
      to={`${routes.index}${routes.patient.root}/${claim.chargePatientId}${routes.billing.root}${routes.billing.charge}${routes.billing.edit}${claim.chargeId}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}>
      {label}
    </Link>
  )
}

const getChargeDetails = (claim: BillingTransactionClaim): JSX.Element => {
  const matchStatus = getClaimMatchStatus(claim)

  if (!claim.chargeId) {
    return <>{matchStatus === 'AMBIGUOUS' ? 'Several charges fit, none picked' : 'No charge matched'}</>
  }

  return (
    <div css={{ textAlign: 'left' }}>
      {matchStatus === 'SUGGESTED' && <div css={{ fontStyle: 'italic' }}>Suggested match</div>}
      <div>Order: {claim.chargeOrderId ?? '-'}</div>
      <div>DOB: {formatCalendarDate(claim.chargePatientDob)}</div>
    </div>
  )
}

// The patient the 835 names, with the policyholder after it when the patient is
// a dependent on someone else's policy. Claims imported before the two were
// kept apart carry one name only.
export const ClaimPatientName = ({ claim }: { claim: BillingTransactionClaim }) => (
  <>
    <TooltipWrapper text={() => getChargeDetails(claim)} show>
      <span css={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
        {claim.patientName || '-'}
      </span>
    </TooltipWrapper>
    {claim.insuredName && (
      <span className="text-muted" title="Policyholder">
        {' '}
        ({claim.insuredName})
      </span>
    )}
  </>
)
